import { useEffect, useState } from "react";
import { FiEye, FiEyeOff, FiMail } from "react-icons/fi";
import "./LoginSign.css";
import { getSalonAdminApiBaseUrl } from "../utils/apiConfig";
import {
  homePathForPortal,
  persistPortalLoginSession,
} from "../utils/portalLoginSession";

const REMEMBER_EMAIL_KEY = "naturals_unified_REMEMBERED_EMAIL";
const REMEMBER_ME_KEY = "naturals_unified_REMEMBER_ME";

export default function NaturalsPortalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBER_ME_KEY) === "true";
    setRememberMe(remembered);
    if (remembered) {
      setEmail(localStorage.getItem(REMEMBER_EMAIL_KEY) || "");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiBase = String(getSalonAdminApiBaseUrl() || "").replace(/\/+$/, "");
    if (!apiBase) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const res = await fetch(`${apiBase}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        setErrorMessage("Unexpected server response.");
        return;
      }
      if (!res.ok || !data.userId) {
        if (res.status === 403 && String(data.error || "").toLowerCase().includes("disabled")) {
          alert("Your account is disabled.\nPlease contact support.");
          return;
        }
        setErrorMessage(data.error || data.message || "Invalid login credentials");
        return;
      }
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email);
        localStorage.setItem(REMEMBER_ME_KEY, "true");
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
        localStorage.setItem(REMEMBER_ME_KEY, "false");
      }
      const portal = data.portal || "workspace";
      await persistPortalLoginSession(portal, data);
      const target = homePathForPortal(portal) || data.redirectPath;
      window.location.assign(target);
    } catch {
      setErrorMessage("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ns-login">
      <div className="ns-login__shell">
        <div className="ns-login__card">
          <form onSubmit={handleSubmit} className="ns-login__form">
            <div className="ns-login__header">
              <h1 className="ns-login__title"><span>Naturals sign in</span></h1>
              <p className="text-sm text-slate-500 mt-2">
                Franchise owners: use your organization email. Dashboard staff: use the username
                your store manager created for you. You will be taken to your assigned portal.
              </p>
            </div>
            <label className="ns-login__field">
              <span className="ns-login__label">Email or username</span>
              <div className="ns-login__control">
                <input
                  type="text"
                  className="ns-login__input"
                  placeholder="Email or staff username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
                <span className="ns-login__affix"><FiMail size={18} /></span>
              </div>
            </label>
            <label className="ns-login__field">
              <span className="ns-login__label">Password</span>
              <div className="ns-login__control">
                <input
                  type={showPassword ? "text" : "password"}
                  className="ns-login__input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="ns-login__affix"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember sign-in
            </label>
            {errorMessage ? <p className="ns-login__field-error">{errorMessage}</p> : null}
            <button type="submit" className="ns-login__submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
